export type ControlId = string;

/** This is the contract used by a consumer to fill in a template with their controls */
export interface Layout {
  /** A section is a grouping of controls in a particular layout. Sections are rendered hoziontally, with a vertical divider between them.
   *
   *
   *     (SingleRowSection)                   (MultiRowSection)
   * --------------                   d
   * |            |   Small           i   Small Small Small Small
   * | Big Button |   Small           v   Small Small Small Small
   * |            |   Small           i   Small Small Small Small
   * |            |                   d
   * --------------                   e
   *                                  r
   */
  sections: Array<Section | LayoutDivider>;
}

type AdditionalAriaProps = {
  id: string;
  labelledBy: string;
};

export type Section = SingleRowSection | MultiRowSection;
export type SectionItem = SingleRowSectionItem | MultiRowSectionItem;

export type SingleRowSection = [SingleRowSectionItem[]];
/** A row can contain a control, a vertical stack of 3 controls, or a divider */
export type SingleRowSectionItem = ControlId | VerticalControlStackWithForProps | LayoutDivider;
/**
 * Up to 3 controls may be stacked vertically.
 * If null is provided for the final control Id, a space will remain for the control.
 * Otherwise the controls will be distributed evenly vertically
 */
export type VerticalControlStack =
  | [ControlId]
  | [ControlId, ControlId | null]
  | [ControlId, ControlId, ControlId | null]
  | [ControlId, null, null];

export type VerticalControlStackWithForProps = VerticalControlStack & { ariaProps?: AdditionalAriaProps };

export type MultiRowSection =
  | [MultiRowSectionItem[], MultiRowSectionItem[]]
  | [MultiRowSectionItem[], MultiRowSectionItem[], MultiRowSectionItem[]];
export type MultiRowSectionItem = ControlId | LayoutDivider;

export enum MLRKeytipLocation {
  Top = 0,
  Middle = 1,
  Bottom = 2,
}

/** A vertical divider between row items */
export interface LayoutDivider {
  type: 'Divider';
}

export function sections(...sections: Array<Section | LayoutDivider>): Layout {
  return { sections };
}

export function singleRow(...controls: SingleRowSectionItem[]): SingleRowSection {
  return [controls];
}

export function multiRow(
  firstRow: MultiRowSectionItem[],
  secondRow: MultiRowSectionItem[],
  thirdRow?: MultiRowSectionItem[]
): MultiRowSection {
  return !thirdRow ? [firstRow, secondRow] : [firstRow, secondRow, thirdRow];
}

export function divider(): LayoutDivider {
  return { type: 'Divider' };
}

export function row(...controlId: Array<ControlId | LayoutDivider>): MultiRowSectionItem[] {
  return [...controlId];
}

/**
 * @param bottom if provided, the stack will have space at the bottom for a control. Otherwise, the stack will be spaced evenly vertically
 */
export function stack(top: string): VerticalControlStack;
export function stack(top: string, bottom: string | null): VerticalControlStack;
export function stack(top: string, middle: string, bottom: string | null): VerticalControlStack;
export function stack(top: string, middle: null, bottom: null): VerticalControlStack;
export function stack(top: string, middle: string, bottom?: string | null): VerticalControlStack;
export function stack(top: string, middle?: string | null, bottom?: string | null): VerticalControlStack {
  if (middle === undefined) return [top];

  if (bottom === undefined) return [top, middle];

  return [top, middle!, bottom];
}

/**
 * This function provides a way for stack to have an associated Id and aria-labelledBy attribute
 * to allow for controls such as an AppLabel to properly identify the group to accessibility tools
 */
export function stackWithAdditionalAriaProps(
  ariaProps: AdditionalAriaProps,
  top: string,
  middle: string,
  bottom?: string | null
): VerticalControlStackWithForProps {
  const verticalControlStack: VerticalControlStackWithForProps = stack(top, middle, bottom);
  verticalControlStack.ariaProps = ariaProps;
  return verticalControlStack;
}
